<template>
  <!-- eslint-disable vue/no-v-html -- linkify escapes HTML, so there is no risk of XSS from this content -->
  <component
    :is="renderElement"
    v-if="!info && hotlinkUrls"
    class="own-type"
    :class="renderClass"
    v-html="linkifiedText"
  />
  <!-- eslint-enable -->

  <component
    :is="renderElement"
    v-else-if="!info && !hotlinkUrls"
    class="own-type"
    :class="renderClass"
    v-text="text"
  />

  <OwnPopover
    v-else
    align-x="right"
    align-y="bottom"
    :slide="['x']"
    trigger-type="hover"
    content-offset="4"
    grow
  >
    <template #trigger="{ on }">
      <component
        :is="renderElement"
        class="own-type__info-trigger w-full"
        :class="renderClass"
        :align="align"
        v-on="on"
        v-text="text"
      />
    </template>
    <template #content>
      <OwnCard class="own-type__info-content" control>
        <OwnType :text="info" />
      </OwnCard>
    </template>
  </OwnPopover>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

defineComponent({
  name: 'OwnType',
})
</script>

<script lang="ts" setup>
import { computed } from 'vue'

import { linkify } from '@/utils/linkify'

import {
  lookupFont,
  lookupFontColor,
  FontColor,
  FontType,
} from '../Common/fonts'
import { OwnCard } from '../OwnCard'
import { OwnPopover } from '../OwnPopover'

const props = withDefaults(
  defineProps<{
    /**
     * @default 'left'
     */
    align?: 'left' | 'center' | 'right'
    color?: FontColor
    /**
     * @default 'none'
     */
    decoration?: 'none' | 'strikethrough'
    el?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'label'
    /**
     * @default false
     */
    hotlinkUrls?: boolean
    info?: string
    /**
     * @default false
     */
    preserveWhitespace?: boolean
    text: string | number
    /**
     * @default 'paragraph'
     */
    variant?: FontType
  }>(),
  {
    align: 'left',
    color: undefined,
    decoration: 'none',
    el: undefined,
    hotlinkUrls: false,
    info: undefined,
    preserveWhitespace: false,
    variant: 'paragraph',
  }
)

const colorClass = computed(() => {
  if (props.color) {
    return lookupFontColor(props.color)
  }

  switch (props.variant) {
    case 'heading':
      return 'text-color-primary'
    case 'title':
      return 'text-color-primary'
    default:
      return 'text-color-secondary'
  }
})

const linkifiedText = computed(() => {
  if (props.hotlinkUrls) {
    return linkify(props.text.toString(), {
      className: 'own-type__link',
    })
  }

  return props.text.toString()
})

const typeClass = computed(() => {
  return lookupFont(props.variant)
})

const renderClass = computed(() => {
  const classes = [
    typeClass.value,
    colorClass.value,
    `text-${props.align}`,
    `decoration-${props.decoration}`,
  ]

  if (props.preserveWhitespace) {
    classes.push(`preserve-whitespace`)
  }

  return classes.join(' ')
})

const renderElement = computed(() => {
  if (props.el) return props.el

  switch (props.variant) {
    case 'heading':
      return 'h1'
    case 'title':
      return 'h3'
    case 'subtitle':
      return 'h5'
    case 'subtitle-small':
      return 'h6'
    case 'button':
      return 'span'
    default:
      return 'p'
  }
})
</script>

<style lang="scss">
.own-type {
  &.preserve-whitespace {
    white-space: pre-line;
  }

  &__info-trigger {
    cursor: help;
    text-decoration: underline;
    text-underline-offset: 4px;
    text-decoration-style: dotted;
  }

  &__info-content {
    padding: 12px;
    min-width: 250px;
  }

  &.own-type__link {
    color: $misc-brand;
  }
}
</style>
